/**
 * studentGenerationAdapter — ce que « Générer une activité » a de plus chez l'élève que chez le
 * parent. Le parcours lui-même est celui du parent (`ui/hooks/useGenerateActivityFlow`, et
 * `adapters/parent/Activities/activityGenerationAdapter`, lus tels quels) ; ce module n'ajoute que
 * les trois règles que le legacy applique au parcours texte de l'élève (`selfCreate`,
 * `tutor/students/GenerateActivityModal.js` + `components/MaterialGenerationForm.js`) :
 *
 * - un chapitre OU des instructions font avancer (`generationFormValidity.js`, la règle même du
 *   backend `MaterialController::generate`) — le parent, lui, exige le chapitre dès que le
 *   catalogue en a ;
 * - les chapitres du prochain contrôle de la matière choisie remontent en tête
 *   (`GenerateActivityModal.js:709-735`) ;
 * - une recherche de chapitre sans résultat bascule vers Instructions avec le texte cherché
 *   (`MaterialGenerationForm.js:137-167`).
 */
import { MIN_INSTRUCTIONS_LENGTH } from "../../parent/Activities/activityGenerationAdapter"
import {
    toUISchoolAssessment,
    type ApiSchoolAssessment,
} from "../../parent/SchoolAssessments/schoolAssessmentListAdapter"
import {
    type UIActivityGenerationDraft,
    type UIActivityGenerationField,
    type UIActivityGenerationMissingField,
    type UISchoolAssessment,
} from "../../../ui/types"

/**
 * Les champs du cadrage de l'élève : tous, sauf les compétences travaillées — leur route
 * (`chapters/{id}/skill-parts`) demande `manage_student_chapters`, que l'élève n'a pas, et le
 * legacy ne monte pas le sélecteur en `selfCreate` (`GenerateActivityModal.js:880-883`).
 */
export const STUDENT_GENERATION_FIELDS: UIActivityGenerationField[] = [
    "scope",
    "chapter",
    "level",
    "duration",
    "instructions",
]

/**
 * Port de `isGenerationFormValid` hors programme de révision (`generationFormValidity.js`) : la
 * classe, la matière, et un chapitre OU au moins cinq caractères d'instructions. Ce qui manque est
 * nommé `chapter` : c'est le champ qui ouvre les deux chemins, et son aide nomme Instructions.
 */
export function missingStudentGenerationField(
    draft: UIActivityGenerationDraft
): UIActivityGenerationMissingField | null {
    if (draft.className == null) return "class"
    if (draft.subjectId == null) return "subject"
    if (draft.chapterId != null || draft.instructions.length >= MIN_INSTRUCTIONS_LENGTH) return null
    return "chapter"
}

/** Une ligne de `GET school-assessments?upcoming=1` : celle du parent, plus ses chapitres liés. */
export type ApiStudentSchoolAssessment = ApiSchoolAssessment & {
    chapters?: { id?: string | number; name?: string | null }[] | null
}

/**
 * Le prochain contrôle de la matière choisie (`GenerateActivityModal.js:716-719` : le premier de la
 * liste servie, dont la matière est celle du brouillon), et ses chapitres liés (`chapters`, pas
 * `chapters_progress` — le legacy lit `assessment.chapters`) : c'est ce que le formulaire remonte
 * en tête de liste. Aucun contrôle pour cette matière, ou aucun chapitre : rien.
 */
export function nextAssessmentForSubject(
    rows: ApiStudentSchoolAssessment[] | null | undefined,
    subjectId: string | number | null
): UISchoolAssessment | null {
    if (subjectId == null) return null
    const row = (rows ?? []).find((assessment) => String(assessment?.school_subject?.id) === String(subjectId))
    const base = toUISchoolAssessment(row)
    if (row == null || base == null) return null

    const chaptersProgress = (row.chapters ?? [])
        .filter((chapter) => chapter?.id != null && !!chapter.name)
        .map((chapter) => ({
            chapter: { id: chapter.id as string | number, name: chapter.name as string, subject: base.subject },
            progress: 0,
        }))
    if (chaptersProgress.length === 0) return null

    return { ...base, chaptersProgress }
}

/**
 * La bascule vers Instructions après une recherche sans résultat : le texte cherché devient les
 * instructions, jamais par-dessus ce qui y était déjà écrit (`MaterialGenerationForm.js:160`). Le
 * legacy le fait taper lettre à lettre ; ici il arrive d'un coup.
 */
export function describeInInstructions(
    draft: UIActivityGenerationDraft,
    searchText: string
): UIActivityGenerationDraft {
    if (draft.instructions.trim() !== "" || searchText === "") return draft
    return { ...draft, instructions: searchText }
}
